import { useMemo } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { apiFetch, ApiClientError } from '@/lib/api';
import { useAuthStore, hasPermission } from '@/stores/auth';
import { deriveRequiredLevelAvailable } from '@/lib/skill-matrix-fallback';
import {
  selectCurrentDevelopmentPlan, type DevelopmentPlanForSelection, type DevelopmentPlanSelection,
} from '@/lib/development-plan-selection';
import type { EmployeeSkillsResponse } from '@/components/EmployeeSkillsPanel';
import { useDevelopmentPlanSelfOriginResolve } from './useDevelopmentPlanSelfOriginResolve';

/**
 * スキル登録表 (行=スキル、列=人材像が求める水準・現状レベル・目標レベル・目標達成時期) の
 * データ取得・行計算ロジック。docs/design/v05-skill-registration-table-redesign-detail.md §3/§4。
 *
 * `SkillMatrixEditor` (mode:'managed') と `SkillMatrixSelfViewTable` (mode:'self') の両方が使う
 * 共有フック。node_id 解決は `GET /employees/:id/node-assignments` (company_admin 限定で機能不全、
 * §2-6) を一切使わず、どのみち必要な `GET /employees/:id/skills`/`GET /me/skills` のレスポンスに
 * 追加された `primary_node_id` に相乗りする (§4-4-1)。
 */

export type SkillMatrixMasterSkill = {
  skill_id: string; code: string; label: string; scale_id: string | null; description?: string | null;
};
export type SkillMatrixMasterLevel = {
  skill_level_id: string; code: string; label: string; sort_order: number;
  scale_id: string | null; behavior_description: string | null;
};
export type SkillMatrixRoleProfile = { role_profile_id: string; code: string; label: string };

export type DevelopmentPlanApi = DevelopmentPlanForSelection & {
  employee_id: string; skill_id: string; target_scale_level_id: string;
  start_date: string | null; due_date: string | null; origin: 'self' | 'manager';
  result_certification_id: string | null; face_disabled: boolean; row_version: number;
};

export type SkillMatrixRow = {
  skillId: string;
  skillLabel: string;
  skillDescription: string | null;
  scaleId: string | null;
  requiredSkillLevelId: string | null;
  requiredBehaviorDescription: string | null;
  currentSkillLevelId: string | null;
  currentSource: 'manual' | 'role_profile' | null;
  currentIsOverridden: boolean;
  currentBehaviorDescription: string | null;
  planSelection: DevelopmentPlanSelection<DevelopmentPlanApi>;
  allPlansForSkill: DevelopmentPlanApi[];
  /** §8-2-1: skill_level_id → 到達基準文。GET /masters/skills/:id の level_criteria から
   *  並列取得する (未取得/未設定の水準は key が無い)。求められる水準・現状レベル・目標レベルの
   *  3箇所とも、この1つのマップをそれぞれの水準IDで引く。 */
  levelCriteria: Record<string, string>;
};

type SkillDetailResponse = {
  skill_id: string;
  level_criteria: { skill_level_id: string; criteria_text: string }[];
};

export type UseSkillMatrixRowsParams =
  | { mode: 'managed'; employeeId: string }
  | { mode: 'self' };

export type UseSkillMatrixRowsResult = {
  rows: SkillMatrixRow[];
  isLoading: boolean;
  isError: boolean;
  notLinkedToEmployee: boolean;
  employeeId: string | null;
  primaryNodeId: string | null;
  assignedRoleProfileIds: string[];
  requiredLevelAvailable: boolean;
  requiredLevelFeatureDisabled: boolean;
  requiredLevelScopeForbidden: boolean;
  developmentPlanFeatureDisabled: boolean;
  canManageSkillLevel: boolean;
  canManageDevelopmentPlan: boolean;
  developmentPlanPermissionResolving: boolean;
  skillsMaster: SkillMatrixMasterSkill[];
  skillLevelsMaster: SkillMatrixMasterLevel[];
  roleProfilesMaster: SkillMatrixRoleProfile[];
};

export function useSkillMatrixRows(params: UseSkillMatrixRowsParams): UseSkillMatrixRowsResult {
  const user = useAuthStore((s) => s.user);
  const perms = user?.effective_permissions;
  const nonInheriting = user?.non_inheriting_features;

  const managedEmployeeId = params.mode === 'managed' ? params.employeeId : undefined;

  const managedQuery = useQuery<EmployeeSkillsResponse>({
    queryKey: ['employee-skills', managedEmployeeId],
    queryFn: () => apiFetch<EmployeeSkillsResponse>(`/employees/${managedEmployeeId}/skills`),
    enabled: params.mode === 'managed' && !!managedEmployeeId,
    retry: false,
  });
  const selfQuery = useQuery<EmployeeSkillsResponse & { not_linked_to_employee: boolean }>({
    queryKey: ['my-skills'],
    queryFn: () => apiFetch('/me/skills'),
    enabled: params.mode === 'self',
    retry: false,
  });

  const baseQuery = params.mode === 'managed' ? managedQuery : selfQuery;
  const notLinkedToEmployee = params.mode === 'self' && selfQuery.data?.not_linked_to_employee === true;
  const employeeId = params.mode === 'managed' ? (managedEmployeeId ?? null) : (selfQuery.data?.employee_id ?? null);
  const primaryNodeId = baseQuery.data?.primary_node_id ?? null;

  const skillsQuery = useQuery<SkillMatrixMasterSkill[]>({ queryKey: ['skills'], queryFn: () => apiFetch('/masters/skills') });
  const skillLevelsQuery = useQuery<SkillMatrixMasterLevel[]>({ queryKey: ['skill-levels'], queryFn: () => apiFetch('/masters/skill-levels') });
  const roleProfilesQuery = useQuery<SkillMatrixRoleProfile[]>({ queryKey: ['role-profiles'], queryFn: () => apiFetch('/masters/role-profiles') });

  // §4-7/§4-8: matrix 取得には主所属 node_id が要る。未解決の間は呼び出し自体を行わない。
  const matrixQuery = useQuery<{
    role_profile_skills: { role_profile_id: string; skill_id: string; min_skill_level_id: string; behavior_description: string | null }[];
  }>({
    queryKey: ['competency-matrix', primaryNodeId],
    queryFn: () => apiFetch(`/competency/matrix?node_id=${encodeURIComponent(primaryNodeId as string)}`),
    enabled: !!primaryNodeId && !notLinkedToEmployee,
    retry: false,
  });
  const {
    requiredLevelAvailable, featureDisabled: requiredLevelFeatureDisabled, scopeForbidden: requiredLevelScopeForbidden,
  } = deriveRequiredLevelAvailable({ nodeId: primaryNodeId, isError: matrixQuery.isError, error: matrixQuery.error });

  const plansQuery = useQuery<DevelopmentPlanApi[]>({
    queryKey: ['development-plans-by-employee', employeeId],
    queryFn: () => apiFetch<DevelopmentPlanApi[]>(`/development-plans?employee_id=${encodeURIComponent(employeeId as string)}`),
    enabled: !!employeeId && !notLinkedToEmployee,
    retry: false,
  });
  const developmentPlanFeatureDisabled = plansQuery.error instanceof ApiClientError && plansQuery.error.code === 'FEATURE_DISABLED';

  const canManageSkillLevel = hasPermission(perms, 'skill', 'manage', nonInheriting);
  const canManageDevelopmentPlanDirect = hasPermission(perms, 'development_plan', 'manage', nonInheriting);
  // §4-4-3 手順2: 直接の development_plan:manage が無い self モードのみ resolve API を叩く。
  const selfOriginResolve = useDevelopmentPlanSelfOriginResolve(
    primaryNodeId, params.mode === 'self' && !canManageDevelopmentPlanDirect,
  );
  const canManageDevelopmentPlan = params.mode === 'self'
    ? (canManageDevelopmentPlanDirect || (!selfOriginResolve.isLoading && selfOriginResolve.enabled))
    : canManageDevelopmentPlanDirect;
  const developmentPlanPermissionResolving = params.mode === 'self' && !canManageDevelopmentPlanDirect && selfOriginResolve.isLoading;

  const assignedRoleProfileIds = useMemo(
    () => (baseQuery.data?.role_profiles ?? []).filter((r) => r.node_id === null).map((r) => r.role_profile_id),
    [baseQuery.data],
  );

  // §4-7 ルール1/2: rows の行を構成するスキルID一覧。GET /masters/skills/:id の並列取得キー
  // (下記 skillDetailQueries) にもそのまま使うため、rows 構築より先に独立した useMemo として持つ。
  const visibleSkillIds = useMemo(() => {
    if (!baseQuery.data) return [] as string[];
    const skillLevelsMaster = skillLevelsQuery.data ?? [];
    const sortOrderByLevel = new Map(skillLevelsMaster.map((l) => [l.skill_level_id, l.sort_order]));
    const plans = plansQuery.data ?? [];
    const requiredSkillIds = requiredLevelAvailable && matrixQuery.data
      ? matrixQuery.data.role_profile_skills
        .filter((r) => assignedRoleProfileIds.includes(r.role_profile_id) && sortOrderByLevel.has(r.min_skill_level_id))
        .map((r) => r.skill_id)
      : [];
    const ids = new Set<string>([
      ...requiredSkillIds,
      ...baseQuery.data.skills.map((s) => s.skill_id),
      ...plans.filter((p) => p.status !== 'cancelled').map((p) => p.skill_id),
    ]);
    return [...ids];
  }, [baseQuery.data, skillLevelsQuery.data, plansQuery.data, matrixQuery.data, requiredLevelAvailable, assignedRoleProfileIds]);

  // §8-2-1: 表示中のスキルIDごとに GET /masters/skills/:id を並列取得し、水準ごとの到達基準文を得る
  // (新規バックエンド変更不要、既存エンドポイントが level_criteria を全水準分返す)。
  const skillDetailQueries = useQueries({
    queries: visibleSkillIds.map((skillId) => ({
      queryKey: ['skill-detail-criteria', skillId],
      queryFn: () => apiFetch<SkillDetailResponse>(`/masters/skills/${encodeURIComponent(skillId)}`),
    })),
  });
  // 実装後レビュー HIGH/MEDIUM是正 (QA・design-and-code 独立検出、2026-09-20): 以前はここで
  // render のたびに新規 Map を生成し、それを下の rows の useMemo 依存に含めていたため、
  // useMemo が実質無効化されていた (GET /masters/skills/:id の取得完了とは無関係に、あらゆる
  // 再レンダリングで rows が再計算されていた)。取得結果 (data) 自体を依存配列に使い、
  // 実際に取得が完了した時だけ再計算されるようにする。
  const skillDetailData = skillDetailQueries.map((q) => q.data);
  const criteriaBySkillId = useMemo(() => {
    const map = new Map<string, Record<string, string>>();
    visibleSkillIds.forEach((skillId, i) => {
      const data = skillDetailData[i];
      if (data) map.set(skillId, Object.fromEntries(data.level_criteria.map((c) => [c.skill_level_id, c.criteria_text])));
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleSkillIds, ...skillDetailData]);

  const rows = useMemo<SkillMatrixRow[]>(() => {
    if (!baseQuery.data) return [];
    const skillsMaster = skillsQuery.data ?? [];
    const skillLevelsMaster = skillLevelsQuery.data ?? [];
    const sortOrderByLevel = new Map(skillLevelsMaster.map((l) => [l.skill_level_id, l.sort_order]));
    const plans = plansQuery.data ?? [];
    const plansBySkill = new Map<string, DevelopmentPlanApi[]>();
    for (const p of plans) plansBySkill.set(p.skill_id, [...(plansBySkill.get(p.skill_id) ?? []), p]);

    // §4-7 ルール1: 選択中人材像群 (assignedRoleProfileIds) の必要スキルを合算し、最も高い水準を採用する
    const requiredBySkill = new Map<string, { skillLevelId: string; sortOrder: number; behaviorDescription: string | null }>();
    if (requiredLevelAvailable && matrixQuery.data) {
      for (const r of matrixQuery.data.role_profile_skills) {
        if (!assignedRoleProfileIds.includes(r.role_profile_id)) continue;
        const sortOrder = sortOrderByLevel.get(r.min_skill_level_id);
        if (sortOrder === undefined) continue; // 現行版でない習熟度は無視 (安全側、employee-skills.tsと同型)
        const existing = requiredBySkill.get(r.skill_id);
        if (!existing || sortOrder > existing.sortOrder) {
          requiredBySkill.set(r.skill_id, { skillLevelId: r.min_skill_level_id, sortOrder, behaviorDescription: r.behavior_description });
        }
      }
    }

    const currentBySkill = new Map(baseQuery.data.skills.map((s) => [s.skill_id, s]));

    return visibleSkillIds.map((skillId) => {
      const master = skillsMaster.find((s) => s.skill_id === skillId);
      const required = requiredBySkill.get(skillId);
      const current = currentBySkill.get(skillId);
      const plansForSkill = plansBySkill.get(skillId) ?? [];
      return {
        skillId,
        skillLabel: master?.label ?? skillId,
        skillDescription: master?.description ?? null,
        scaleId: master?.scale_id ?? null,
        requiredSkillLevelId: required?.skillLevelId ?? null,
        requiredBehaviorDescription: required?.behaviorDescription ?? null,
        currentSkillLevelId: current?.skill_level_id ?? null,
        currentSource: (current?.source as 'manual' | 'role_profile' | undefined) ?? null,
        currentIsOverridden: current?.is_overridden ?? false,
        currentBehaviorDescription: current?.behavior_description ?? null,
        planSelection: selectCurrentDevelopmentPlan(plansForSkill),
        allPlansForSkill: plansForSkill,
        levelCriteria: criteriaBySkillId.get(skillId) ?? {},
      };
    }).sort((a, b) => a.skillLabel.localeCompare(b.skillLabel, 'ja'));
    // criteriaBySkillId は取得結果が変わった時だけ参照が変わる useMemo (上記) のため、依存に含めても
    // GET /masters/skills/:id の各取得が完了した時のみ rows が再計算される (取得完了前は levelCriteria
    // が空のまま表示され、完了後に基準文が現れる)。
  }, [baseQuery.data, skillsQuery.data, skillLevelsQuery.data, plansQuery.data, matrixQuery.data, requiredLevelAvailable, assignedRoleProfileIds, visibleSkillIds, criteriaBySkillId]);

  return {
    rows,
    isLoading: baseQuery.isLoading || skillsQuery.isLoading || skillLevelsQuery.isLoading,
    isError: baseQuery.isError,
    notLinkedToEmployee,
    employeeId,
    primaryNodeId,
    assignedRoleProfileIds,
    requiredLevelAvailable,
    requiredLevelFeatureDisabled,
    requiredLevelScopeForbidden,
    developmentPlanFeatureDisabled,
    canManageSkillLevel,
    canManageDevelopmentPlan,
    developmentPlanPermissionResolving,
    skillsMaster: skillsQuery.data ?? [],
    skillLevelsMaster: skillLevelsQuery.data ?? [],
    roleProfilesMaster: roleProfilesQuery.data ?? [],
  };
}
